import { useCallback, useEffect, useState } from "react";
import {
  ANNOUNCEMENT_APP_VERSIONS,
  ANNOUNCEMENT_CAMPAIGN_SEGMENTS,
  ANNOUNCEMENT_LIMITS,
  type AnnouncementCampaignDraft,
  type AnnouncementCampaignView,
  type AnnouncementDryRun,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  fetchCampaigns,
  fetchDryRun,
  saveCampaign,
  sendCampaign,
} from "@/lib/announcement-campaigns-api";
import {
  HELD_LABEL,
  SEGMENT_LABEL,
  buildRangeText,
  campaignErrorText,
  draftOf,
  emptyDraft,
  longDate,
  pushBodyHint,
  sendState,
} from "@/lib/announcement-campaigns";

// TES-1676 — "Utskick": the admin writes a campaign's push and Hem card,
// sees how many in each segment the push would reach (counts only, no
// names), and sends it with the campaign key typed as confirmation. The
// Hem card itself reaches the app in a later slice (S2); the push opens the
// booking.

const FIELD =
  "w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand disabled:bg-surfaceMuted/30";
const PRIMARY =
  "px-md py-sm rounded-md bg-brand text-white text-sm disabled:opacity-50";
const SECONDARY =
  "px-md py-sm rounded-md border border-surfaceMuted text-sm text-textSecondary disabled:opacity-50";

function todayIso(): string {
  return new Date().toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
  });
}

function when(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function AnnouncementCampaignsRoute() {
  const { signOut } = useAuth();
  const [campaigns, setCampaigns] = useState<AnnouncementCampaignView[] | null>(
    null,
  );
  const [saved, setSaved] = useState<AnnouncementCampaignView | null>(null);
  const [draft, setDraft] = useState<AnnouncementCampaignDraft>(emptyDraft);
  const [counts, setCounts] = useState<AnnouncementDryRun | null>(null);
  const [countsError, setCountsError] = useState(false);
  const [confirmKey, setConfirmKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) return void signOut();
      setError(campaignErrorText(e instanceof Error ? e.message : ""));
    },
    [signOut],
  );

  useEffect(() => {
    fetchCampaigns().then(setCampaigns).catch(handleError);
  }, [handleError]);

  // The counts follow the inputs that change them: the build range.
  const { minAppVersion, maxAppVersion } = draft;
  useEffect(() => {
    let live = true;
    setCountsError(false);
    fetchDryRun({ minAppVersion, maxAppVersion })
      .then((c) => live && setCounts(c))
      .catch((e) => {
        if (e instanceof UnauthorizedError) return void signOut();
        if (live) setCountsError(true);
      });
    return () => {
      live = false;
    };
  }, [minAppVersion, maxAppVersion, signOut]);

  const sent = saved?.status === "sent";
  const set = <K extends keyof AnnouncementCampaignDraft>(
    k: K,
    v: AnnouncementCampaignDraft[K],
  ) => setDraft((d) => ({ ...d, [k]: v }));

  function open(c: AnnouncementCampaignView | null) {
    setSaved(c);
    setDraft(c ? draftOf(c) : emptyDraft());
    setConfirmKey("");
    setError(null);
    setNotice(null);
  }

  function replace(next: AnnouncementCampaignView) {
    setSaved(next);
    setDraft(draftOf(next));
    setCampaigns((list) => [
      next,
      ...(list ?? []).filter((c) => c.id !== next.id),
    ]);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      replace(await saveCampaign(saved?.id ?? null, draft));
      setNotice("Utkastet är sparat.");
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!saved) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await sendCampaign(saved.id, confirmKey);
      replace(next);
      setConfirmKey("");
      setNotice(
        `Utskicket är skickat till ${next.sendCounts?.recipients ?? 0} patienter. Pusharna går ut under de närmaste minuterna.`,
      );
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  const hint = pushBodyHint(draft.pushBody);
  const state = sendState(saved, draft, confirmKey, todayIso());
  const segmentCounts = counts?.bySegment[draft.segment];

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Utskick</h1>
      <p className="text-sm text-textSecondary max-w-prose">
        Skriv en push till en grupp patienter och texten på kortet de ser på
        Hem. Antalen nedan är bara antal, inga namn. Ett skickat utskick kan
        inte ändras.
      </p>

      {error ? (
        <div
          role="alert"
          className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm"
        >
          {error}
        </div>
      ) : null}
      {notice ? (
        <div
          role="status"
          className="bg-success/10 border border-success/30 rounded-md p-md text-sm"
        >
          {notice}
        </div>
      ) : null}

      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-sm">
          <h2 className="text-lg font-medium">Utskick</h2>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => open(null)}
          >
            Nytt utskick
          </button>
        </div>
        {campaigns === null ? (
          <p className="text-sm text-textSecondary">Hämtar utskick…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-sm text-textSecondary">
            Inga utskick ännu. Fyll i formuläret nedan för att skapa det första.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-surfaceMuted">
            {campaigns.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => open(c)}
                  aria-current={saved?.id === c.id}
                  className="w-full text-left py-sm flex flex-wrap gap-x-md gap-y-xs text-sm hover:bg-surfaceMuted/30 aria-[current=true]:font-medium"
                >
                  <span className="font-mono">{c.key}</span>
                  <span className="text-textSecondary">
                    {SEGMENT_LABEL[c.segment]}
                  </span>
                  <span className="text-textSecondary">
                    {c.status === "sent" && c.sentAt
                      ? `Skickat ${when(c.sentAt)} · ${c.sendCounts?.recipients ?? 0} mottagare`
                      : "Utkast"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
        <h2 className="text-lg font-medium">
          {saved ? `Utskick ${saved.key}` : "Nytt utskick"}
          {sent ? " (skickat)" : ""}
        </h2>

        <fieldset disabled={sent || busy} className="flex flex-col gap-sm">
          <label className="flex flex-col gap-xs text-sm">
            Nyckel
            <input
              className={`${FIELD} font-mono`}
              value={draft.key}
              placeholder="t.ex. 2026-11-kostnadsfritt-vardmote"
              onChange={(e) => set("key", e.target.value)}
              aria-describedby="nyckel-hjalp"
            />
            <span id="nyckel-hjalp" className="text-xs text-textSecondary">
              Små bokstäver, siffror och bindestreck. Den skrivs igen för att
              bekräfta utskicket.
            </span>
          </label>

          <label className="flex flex-col gap-xs text-sm">
            Mottagare
            <select
              className={FIELD}
              value={draft.segment}
              onChange={(e) =>
                set(
                  "segment",
                  e.target.value as AnnouncementCampaignDraft["segment"],
                )
              }
            >
              {ANNOUNCEMENT_CAMPAIGN_SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  {SEGMENT_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-xs text-sm">
            Pushrubrik
            <input
              className={FIELD}
              value={draft.pushTitle}
              maxLength={ANNOUNCEMENT_LIMITS.pushTitle}
              onChange={(e) => set("pushTitle", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-xs text-sm">
            Pushtext
            <textarea
              className={FIELD}
              rows={3}
              value={draft.pushBody}
              maxLength={ANNOUNCEMENT_LIMITS.pushBody}
              onChange={(e) => set("pushBody", e.target.value)}
              aria-describedby="pushtext-hjalp"
            />
            <span
              id="pushtext-hjalp"
              className={`text-xs ${hint.tooLong ? "text-warning" : "text-textSecondary"}`}
            >
              {hint.text}
            </span>
          </label>

          <label className="flex flex-col gap-xs text-sm">
            Rubrik på Hem-kortet
            <input
              className={FIELD}
              value={draft.cardTitle}
              maxLength={ANNOUNCEMENT_LIMITS.cardTitle}
              onChange={(e) => set("cardTitle", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-xs text-sm">
            Text på Hem-kortet
            <textarea
              className={FIELD}
              rows={6}
              value={draft.cardBody}
              maxLength={ANNOUNCEMENT_LIMITS.cardBody}
              onChange={(e) => set("cardBody", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-xs text-sm">
            Knapptext
            <input
              className={FIELD}
              value={draft.buttonLabel}
              maxLength={ANNOUNCEMENT_LIMITS.buttonLabel}
              onChange={(e) => set("buttonLabel", e.target.value)}
            />
          </label>

          <div className="flex flex-col gap-xs text-sm">
            <label className="flex items-center gap-sm">
              <input
                type="checkbox"
                checked={draft.freeMeetingUntil !== null}
                onChange={(e) =>
                  set("freeMeetingUntil", e.target.checked ? todayIso() : null)
                }
              />
              Kostnadsfritt vårdmöte till och med
            </label>
            {draft.freeMeetingUntil !== null ? (
              <input
                type="date"
                className={`${FIELD} max-w-xs`}
                value={draft.freeMeetingUntil}
                min={todayIso()}
                onChange={(e) =>
                  set("freeMeetingUntil", e.target.value || todayIso())
                }
                aria-label="Sista dag för kostnadsfritt vårdmöte"
              />
            ) : null}
            <span className="text-xs text-textSecondary">
              Den som får utskicket betalar 0 kr för sitt första vårdmöte, en
              gång, om hon bokar senast den dagen. Ingen rabattkod behövs.
            </span>
          </div>

          <div className="flex flex-col gap-xs text-sm">
            <span>Appversioner (valfritt)</span>
            <div className="flex flex-wrap gap-sm">
              <label className="flex flex-col gap-xs text-xs text-textSecondary">
                Lägsta version
                <select
                  className={FIELD}
                  value={draft.minAppVersion ?? ""}
                  onChange={(e) =>
                    set(
                      "minAppVersion",
                      (e.target.value ||
                        null) as AnnouncementCampaignDraft["minAppVersion"],
                    )
                  }
                >
                  <option value="">Ingen gräns</option>
                  {ANNOUNCEMENT_APP_VERSIONS.map((v) => (
                    <option key={v.version} value={v.version}>
                      {v.version}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-xs text-xs text-textSecondary">
                Högsta version
                <select
                  className={FIELD}
                  value={draft.maxAppVersion ?? ""}
                  onChange={(e) =>
                    set(
                      "maxAppVersion",
                      (e.target.value ||
                        null) as AnnouncementCampaignDraft["maxAppVersion"],
                    )
                  }
                >
                  <option value="">Ingen gräns</option>
                  {ANNOUNCEMENT_APP_VERSIONS.map((v) => (
                    <option key={v.version} value={v.version}>
                      {v.version}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <span className="text-xs text-textSecondary">
              {buildRangeText(draft)}. Utan gräns går utskicket till alla
              appversioner. Versionen läses av vad appen senast rapporterade;
              2.0.9 och 2.0.10 räknas som 2.0.11. Den som aldrig har rapporterat
              en version räknas bort när en gräns är satt.
            </span>
          </div>
        </fieldset>

        {!sent ? (
          <div className="flex flex-wrap gap-sm">
            <button
              type="button"
              className={PRIMARY}
              disabled={busy}
              onClick={save}
            >
              Spara utkast
            </button>
          </div>
        ) : null}

        <div className="grid gap-md md:grid-cols-2">
          <div className="flex flex-col gap-xs">
            <h3 className="text-sm font-medium">Förhandsvisning: push</h3>
            <div className="rounded-lg border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm">
              <p className="text-xs text-textSecondary">Ella · nu</p>
              <p className="font-medium">{draft.pushTitle || "Pushrubrik"}</p>
              <p className="line-clamp-4 whitespace-pre-wrap">
                {draft.pushBody || "Pushtext"}
              </p>
            </div>
            <p className="text-xs text-textSecondary">
              Ett tryck på pushen öppnar bokningen av vårdmötet.
            </p>
          </div>
          <div className="flex flex-col gap-xs">
            <h3 className="text-sm font-medium">Förhandsvisning: Hem-kortet</h3>
            <div className="rounded-lg border border-surfaceMuted p-md text-sm flex flex-col gap-sm">
              <p className="font-medium">{draft.cardTitle || "Rubrik"}</p>
              <p className="whitespace-pre-wrap">{draft.cardBody || "Text"}</p>
              <span className="self-start rounded-md bg-brand px-md py-xs text-white">
                {draft.buttonLabel || "Knapptext"}
              </span>
            </div>
            <p className="text-xs text-textSecondary">
              Kortet visas i appen i nästa steg av bygget.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
        <h2 className="text-lg font-medium">
          {sent ? "Utfall vid utskicket" : "Antal just nu"}
        </h2>
        {sent && saved?.sendCounts ? (
          <p className="text-sm">
            {saved.sendCounts.recipients} mottagare.{" "}
            {saved.sendCounts.enqueued.scheduled} pushar köades,{" "}
            {saved.sendCounts.enqueued.duplicate} låg redan i kön.
          </p>
        ) : null}
        {countsError ? (
          <p className="text-sm text-danger">
            Antalen kunde inte hämtas. Ladda om sidan för att försöka igen.
          </p>
        ) : !counts ? (
          <p className="text-sm text-textSecondary">Räknar…</p>
        ) : (
          <div className="grid gap-md md:grid-cols-2">
            {ANNOUNCEMENT_CAMPAIGN_SEGMENTS.map((s) => {
              const c = (sent ? saved?.sendCounts : counts)?.bySegment[s];
              if (!c) return null;
              const chosen = s === draft.segment;
              return (
                <div
                  key={s}
                  className={`rounded-md border p-sm text-sm ${chosen ? "border-brand" : "border-surfaceMuted"}`}
                >
                  <p className="font-medium">{SEGMENT_LABEL[s]}</p>
                  <p className="text-2xl font-display">{c.eligible}</p>
                  <p className="text-xs text-textSecondary">
                    {chosen ? "nås av pushen" : "kan nås (inte vald)"}
                  </p>
                  <dl className="mt-xs grid grid-cols-[1fr_auto] gap-x-sm text-xs text-textSecondary">
                    {(
                      Object.keys(HELD_LABEL) as Array<keyof typeof HELD_LABEL>
                    ).map((k) => (
                      <div key={k} className="contents">
                        <dt>{HELD_LABEL[k]}</dt>
                        {/* A campaign sent before TES-1681 stored no cannot_book. */}
                        <dd className="text-right">{c[k] ?? "–"}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {!sent ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
          <h2 className="text-lg font-medium">Skicka</h2>
          <p className="text-sm">
            Pushen går till{" "}
            <strong>{segmentCounts?.eligible ?? "…"} patienter</strong> i
            gruppen "{SEGMENT_LABEL[draft.segment]}" ({buildRangeText(draft)}).
            {draft.freeMeetingUntil
              ? ` De får sitt första vårdmöte kostnadsfritt om de bokar senast ${longDate(draft.freeMeetingUntil)}.`
              : ""}
          </p>
          <label className="flex flex-col gap-xs text-sm max-w-md">
            Skriv utskickets nyckel för att bekräfta
            <input
              className={`${FIELD} font-mono`}
              value={confirmKey}
              onChange={(e) => setConfirmKey(e.target.value)}
              disabled={!saved || busy}
              autoComplete="off"
            />
          </label>
          {!state.canSend ? (
            <p className="text-sm text-textSecondary">{state.reason}</p>
          ) : null}
          <div>
            <button
              type="button"
              className={PRIMARY}
              disabled={busy || !state.canSend}
              onClick={send}
            >
              Skicka utskicket
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
